import { useRouteLoaderData, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { OrganizationWithWorkspaces } from '@/types/Organization';
import { WorkspaceFromStore } from '@/types/workspaces';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { OrganizationsService } from '@/services/organizationsService';
import { find } from 'lodash';
import { auth } from '@/firebase/init';
import { useUserWorkspacesInvitations } from '@/services/queries/useUserWorkspacesInvitations';
import { useUserOrganizationsInvitations } from '@/services/queries/useUserOrganizationInvitations';

export const useWorkspaceData = () => {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspaceId') as string;
  const { workspaces, userId } = useRouteLoaderData('workspaces') as {
    workspaces: WorkspaceFromStore[];
    userId: string;
  };
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  const permission = useUserPermissionsStore((state) => state.organizationPermission);
  const organizations = useOrganizationStore((state) => state.organizations);
  const isEmailVerified = useUserPermissionsStore((state) => state.isEmailVerified);

  const [localSelectedOrganization, setLocalSelectedOrganization] =
    useState<OrganizationWithWorkspaces | null>(selectedOrganization);
  const [localOrganizationPermission, setLocalOrganizationPermission] =
    useState(permission);
  const [prevSelectedOrganizationId, setPrevSelectedOrganizationId] = useState(
    selectedOrganization?.id,
  );

  const organizationInvitations = useUserOrganizationsInvitations(userId);
  const invitations = useUserWorkspacesInvitations(userId);

  if (prevSelectedOrganizationId !== selectedOrganization?.id) {
    setPrevSelectedOrganizationId(selectedOrganization?.id);
    setLocalSelectedOrganization(selectedOrganization);
  }

  if (!localSelectedOrganization && selectedOrganization) {
    setLocalSelectedOrganization(selectedOrganization);
  }

  useEffect(() => {
    const updatedLocalOrganization = find(
      organizations,
      (org) => org.id === localSelectedOrganization?.id,
    );
    if (!updatedLocalOrganization) return;
    setLocalSelectedOrganization(updatedLocalOrganization);
  }, [localSelectedOrganization?.id, organizations]);

  useEffect(() => {
    if (!localSelectedOrganization || !auth.currentUser?.uid) return;
    OrganizationsService.getUserPermission(
      localSelectedOrganization?.id,
      auth.currentUser?.uid,
    )
      .then((data) => setLocalOrganizationPermission(data))
      .catch((error) => console.error(error));
  }, [localSelectedOrganization]);

  const setNewlyCreatedOrganizationAsLocalSelectedOrganization = useCallback(
    (organizationId: string) => {
      const organization = find(organizations, (org) => org.id === organizationId);
      if (organization) {
        setLocalSelectedOrganization(organization);
      }
    },
    [organizations],
  );

  return {
    workspaceId,
    workspaces,
    userId,
    selectedOrganization,
    localSelectedOrganization,
    setLocalSelectedOrganization,
    localOrganizationPermission,
    invitations,
    organizationInvitations,
    isEmailVerified,
    setNewlyCreatedOrganizationAsLocalSelectedOrganization,
  };
};
